import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { Panel } from '../../panels/panel';
import { QueueStateProvider } from '../context/queue-state-provider';
import {
    isBoloQueue,
    isEventQueue,
    isScheduledEventQueue,
    isUnitQueue,
} from '../utils/queue-type-utils';
import { Queue } from './queue';
import { QueueColumnSelector } from './queue-column-selector';
import { QueueHeader } from './queue-header';
import { QueueFilterIndicator } from './queue-filter-indicator';
import {
    useOpenEventPanel,
    useOpenScheduledEventPanel,
} from '../../event-panel/use-open-event-panel';
import { useOpenBoloPanel } from '../../bolos/bolo-panel/use-open-bolo-panel';

export const QueuePanel = observer(function QueuePanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    const [showColumnSelection, setShowColumnSelection] = useState(false);
    const openEventPanel = useOpenEventPanel();
    const openScheduledEventPanel = useOpenScheduledEventPanel();
    const openBoloPanel = useOpenBoloPanel();

    const onRowDoubleClick = useCallback(
        (row: unknown): void => {
            if (isEventQueue(panel, row)) {
                openEventPanel({
                    eventId: row.id,
                });
            } else if (isScheduledEventQueue(panel, row)) {
                openScheduledEventPanel({ eventId: row.id });
            } else if (isUnitQueue(panel, row)) {
                if (row.eventQueueView?.id) {
                    openEventPanel({
                        eventId: row.eventQueueView.id,
                    });
                }
            } else if (isBoloQueue(panel, row)) {
                openBoloPanel({ boloId: row.id });
            }
        },
        [panel, openEventPanel, openScheduledEventPanel, openBoloPanel]
    );

    const onClose = useCallback(() => panel.close(), [panel]);

    const handleEditColumnsClick = useCallback(() => setShowColumnSelection(true), [
        setShowColumnSelection,
    ]);
    const handleColumnSelectorClose = useCallback(() => setShowColumnSelection(false), [
        setShowColumnSelection,
    ]);

    return (
        <QueueStateProvider workspacePanel={panel}>
            <Panel
                panel={panel}
                header={<QueueHeader onEditColumns={handleEditColumnsClick} />}
                body={
                    <>
                        <QueueFilterIndicator />
                        <Queue onRowDoubleClick={onRowDoubleClick} onClose={onClose} />
                        {showColumnSelection ? (
                            <QueueColumnSelector onClose={handleColumnSelectorClose} />
                        ) : null}
                    </>
                }
            />
        </QueueStateProvider>
    );
});
